// Panels are still written as HTML strings, but they are never swapped in
// wholesale: patch() walks the live DOM and changes only what differs (text,
// attributes, added or removed nodes). Nodes under the pointer keep their
// identity, so clicks, hover, focus, tooltips and loaded sprites survive the
// several updates a second the hunt sends. Children with a data-key attribute
// are matched by key, so a reordered list moves nodes instead of re-creating them.

const tpl = document.createElement('template');
const lastHtml = new WeakMap<Element, string>();

export function patch(host: Element | null, html: string) {
  if (!host || lastHtml.get(host) === html) return;
  lastHtml.set(host, html);
  tpl.innerHTML = html;
  morphChildren(host, tpl.content);
}

const keyOf = (n: Node | null) => (n && n.nodeType === 1 ? (n as Element).getAttribute('data-key') : null);

function sameKind(a: Node, b: Node) {
  if (a.nodeType !== b.nodeType) return false;
  if (a.nodeType !== 1) return true;
  const ea = a as Element, eb = b as Element;
  if (ea.tagName !== eb.tagName) return false;
  return ea.tagName !== 'INPUT' || (ea as HTMLInputElement).type === (eb as HTMLInputElement).type;
}

function morphChildren(cur: Node, next: Node) {
  const keyed = new Map<string, Node>();
  for (let c = cur.firstChild; c; c = c.nextSibling) { const k = keyOf(c); if (k) keyed.set(k, c); }
  let ref = cur.firstChild;
  for (const n of [...next.childNodes]) {
    const k = keyOf(n);
    let match: Node | null = null;
    if (k) { const m = keyed.get(k); if (m && sameKind(m, n)) { match = m; keyed.delete(k); } }
    else if (ref && !keyOf(ref) && sameKind(ref, n)) match = ref;
    if (!match) { cur.insertBefore(n, ref); continue; }
    if (match === ref) ref = ref.nextSibling;
    else cur.insertBefore(match, ref);
    morphNode(match, n);
  }
  while (ref) { const nx = ref.nextSibling; cur.removeChild(ref); ref = nx; }
}

function morphNode(cur: Node, next: Node) {
  if (cur.nodeType !== 1) {
    if (cur.nodeValue !== next.nodeValue) cur.nodeValue = next.nodeValue;
    return;
  }
  const a = cur as HTMLElement, b = next as HTMLElement;
  // A canvas sizes its own backing store (width, height); dropping those would clear what it drew.
  // An icon keeps the file assets.ts watchArtIcons picked for its size while it still shows the same image.
  const own = a.tagName === 'CANVAS' ? (n: string) => n === 'width' || n === 'height'
    : a.tagName === 'IMG' && a.dataset.ic !== undefined && a.getAttribute('src') === b.getAttribute('src') && !b.hasAttribute('srcset') ? (n: string) => n === 'srcset' || n === 'data-ic'
    // A fold the player opened or closed stays that way.
    : a.tagName === 'DETAILS' ? (n: string) => n === 'open'
    : () => false;
  for (const at of [...a.attributes]) if (!b.hasAttribute(at.name) && !own(at.name)) a.removeAttribute(at.name);
  for (const at of [...b.attributes]) if (a.getAttribute(at.name) !== at.value) a.setAttribute(at.name, at.value);
  const focused = document.activeElement === a;
  if (a.tagName === 'INPUT') {
    const ia = a as HTMLInputElement, ib = b as HTMLInputElement;
    // Never overwrite what someone is typing; otherwise follow the markup.
    if (!focused) {
      if (ia.type === 'checkbox' || ia.type === 'radio') ia.checked = ib.hasAttribute('checked');
      else if (ia.value !== (ib.getAttribute('value') ?? '')) ia.value = ib.getAttribute('value') ?? '';
    }
    return;
  }
  if (a.tagName === 'TEXTAREA') return;
  morphChildren(a, b);
  if (a.tagName === 'SELECT' && !focused) {
    const sel = a as HTMLSelectElement;
    const want = [...sel.options].findIndex((o) => o.hasAttribute('selected'));
    if (want >= 0 && sel.selectedIndex !== want) sel.selectedIndex = want;
  }
}

// While a button is held down, hold back updates: a click needs the same node
// under the pointer at press and at release. Queued work runs right after.
let pressed = false;
const queued = new Set<() => void>();
export function whenReleased(fn: () => void): boolean {
  if (!pressed) return false;
  queued.add(fn);
  return true;
}
document.addEventListener('pointerdown', () => { pressed = true; }, true);
const release = () => {
  if (!pressed) return;
  pressed = false;
  // After the click event has been dispatched.
  setTimeout(() => { const fns = [...queued]; queued.clear(); for (const fn of fns) fn(); }, 0);
};
document.addEventListener('pointerup', release, true);
document.addEventListener('pointercancel', release, true);
window.addEventListener('blur', release);
